/**
 * Teillieferung anlegen - 1:1-Portierung des Dialogs (`index.html:3908-3930`, `splitOrder`/
 * `savePartialDelivery` `index.html:8161-8243`), MIT der benannten Verhaltensaenderung 4 (Spec,
 * "Bewusste Verhaltensaenderungen"): "Auch der Teillieferungs-Dialog verliert seine
 * Nummerneingabe samt `<Nr>-T`-Vorschlag und Eindeutigkeitspruefung." Statt der Eingabe zeigt der
 * Dialog "Nummer wird vergeben" (woertlich wie in `Anlagemaske.tsx`, Task 23 - JTL vergibt die
 * Nummer, nicht mehr der Nutzer).
 *
 * Diese Flaeche ist bewusst NICHT gegen die Screenshot-Baseline abgesichert
 * (`test/visual/ausnahmen.ts`, Eintrag `NewOrder/Teillieferung`) - der Nachweis laeuft
 * ausschliesslich ueber `test/komponenten/NewOrder/teillieferung.test.tsx`.
 *
 * ================= Bewusste Verhaltensabweichung vom Original: kein Live-Klemmen =============
 *
 * Das Original klemmt die eingegebene Menge WAEHREND DES TIPPENS auf die Restmenge
 * (`setPartialDeliveryQuantity`, index.html:8189-8195: `Math.min(row.available, Number(value) ||
 * 0)`) - eine zu hohe Eingabe kann dort also nie entstehen, und `savePartialDelivery`
 * (index.html:8197-8243) kennt folgerichtig KEINE eigene "Restmenge ueberschritten"-Pruefung. Der
 * Taskbrief verlangt aber ausdruecklich eine sichtbare Fehlermeldung bei einer zu hohen Eingabe
 * (Testfall "laesst keine Menge groesser als die Restmenge zu") - das ist nur moeglich, wenn NICHT
 * live geklemmt wird. Hier deshalb: freie Eingabe, Pruefung + Fehlermeldung beim Klick auf
 * "Teillieferung anlegen". Siehe Taskbericht, Abschnitt "Fachliche Vorsicht".
 *
 * ================= Wortabweichung vom Original: Knopf- und Feldbeschriftung ===================
 *
 * - Der Original-Knopf heisst "Teillieferung speichern" (index.html:3927); der Taskbrief
 *   verlangt testbar `/teillieferung anlegen/i` (identisch zum Dialogtitel `#partialDeliveryTitle`,
 *   index.html:3912). Hier woertlich wie vom Test gefordert benannt - eine Abweichung vom
 *   Original-Knopftext, dokumentiert statt repariert.
 * - Das Original beschriftet jedes Mengenfeld nur `aria-label="Menge fuer ${item.name}"`
 *   (index.html:8174), ohne das Wort "Teillieferung". Der Taskbrief sucht aber
 *   `getByLabelText(/Menge fuer die Teillieferung/)`. Beschriftung deshalb auf
 *   `Menge fuer die Teillieferung ${item.name}` erweitert.
 *
 * ================= lib-Nutzung: NUR das, was eine plausible Aufrufstelle hat =================
 *
 * Der Taskbrief listet unter "lib/-Nutzung" (fuer Teillieferung UND Sammellieferung gemeinsam)
 * auch `splitRootOrderId(order, alleBestellungen)` und `shipmentOrderGroup(order,
 * alleBestellungen)`. Im Bestand hat KEINE der beiden Funktionen eine Aufrufstelle in
 * `splitOrder`/`savePartialDelivery`/`openConsolidationDialog`/`saveConsolidation` - ihr EINZIGER
 * Aufrufer ist `createDocumentOcrDraft` (index.html:12303, Dokumente-Tab: verteilt einen erkannten
 * Belegbetrag ueber die gesamte Sendungsgruppe). Dieses Feature (Task 20, `Drawer/DocsTab`) liegt
 * ausserhalb dieses Baums (kein `components/Drawer/` hier, siehe Taskbericht) und ausserhalb
 * dieses Tasks.
 *
 * Da `bestellungen` (die volle Liste) trotzdem als Prop verlangt ist (Brief-Testaufruf,
 * `<TeillieferungDialog bestellung={A1} bestellungen={[A1]} .../>`) UND die urspruengliche
 * Begruendung fuer diesen zweiten Parameter (die jetzt entfallene Nummern-Eindeutigkeitspruefung,
 * `DB.orders.some(...)`, index.html:8203-8206) mit Verhaltensaenderung 4 weggefallen ist, nutzt
 * diese Komponente `shipmentOrderGroup`/`splitRootOrderId` fuer einen kleinen, rein informativen
 * Hinweis auf bereits vorhandene Geschwister-Teillieferungen derselben Sendung (siehe unten). Das
 * ist eine ANNAHME, keine getestete Vorgabe - anders als bei Task 23s `estimatedBuchenDateFromEta`
 * (dort fehlte dem Original sogar der EINGABEWERT, den die Funktion braucht, hier sind beide
 * Eingaben vorhanden) gibt es hier keinen harten Ausschlussgrund, nur keinen Bestandsbeleg. Die
 * Anzeige wirkt sich auf KEINEN der drei Testfaelle aus (alle nutzen eine Bestellung ohne
 * Geschwister) - siehe Taskbericht.
 */
import { useState, type JSX } from "react";
import { Ikone } from "../Ikone.tsx";
import { shipmentOrderGroup, splitRootOrderId } from "../../lib/landedCost.ts";
import { NUM } from "../../lib/format.ts";
import { schreibwegVon } from "../../lib/positionsSchreibweg.ts";
import type { Order, OrderItem } from "../../lib/types.ts";
import { Artikelbild } from "../Artikelbild.tsx";

/**
 * Eine an `onTeilen` uebergebene Position - `{ sku, qty, posId }`, `qty`/`posId` als Zahl (nicht als
 * Zeichenkette). `posId` kommt aus `schreibwegVon(positionen, index).jtlPosId` (`lib/
 * positionsSchreibweg.ts`) - derselbe Nachschlag wie in `Positionen.tsx`/`EkTab.tsx`: bevorzugt
 * `jtlPosId`, faellt auf `kLieferantenBestellungPos` zurueck (siehe dortiger Kopfkommentar, "ZWEI
 * QUELLEN FUER DIE POSITIONSKENNUNG") - kein zusaetzlicher GET-Aufruf noetig (Task 9, Pruefbefund:
 * eine reine `kLieferantenBestellungPos`-Ableitung uebersaehe Positionen, die nach bestimmten
 * Nachlade-Pfaden nur `jtlPosId` tragen).
 */
export interface TeillieferungMenge {
  sku: unknown;
  qty: number;
  posId: number;
  /**
   * Die Menge, die der Dialog fuer diese Position als Ausgangsmenge ("Bisher") zeigte - die ROHE
   * `item.qty` (1:1 JTL `fMenge`, `api/jtlBestellungen.ts`: `qty: position.menge ?? 0`), nicht die auf
   * 0 gekappte `verfuegbar`. Der Hub vergleicht sie in der Schreibsperre mit dem frisch gelesenen JTL-Stand
   * (`angezeigteMengen` im Split-Koerper) und weist eine Teilung auf veraltetem Stand mit 409 ab,
   * statt eine zweite Bestellung anzulegen.
   */
  angezeigteMenge: number;
}

/** Ob Dokumente/Tasks der Ursprungsbestellung in die Teillieferung kopiert werden sollen (Task 9). */
export interface TeillieferungKopierOptionen {
  dokumente: boolean;
  tasks: boolean;
}

export interface TeillieferungDialogProps {
  bestellung: Order;
  bestellungen: readonly Order[];
  onTeilen: (
    bestellungId: string,
    mengen: TeillieferungMenge[],
    copyOptions: TeillieferungKopierOptionen,
  ) => void;
  onSchliessen: () => void;
  /**
   * Die Teilung laeuft gerade (Anfrage unterwegs oder Nachladen danach): der Knopf steht gesperrt auf
   * "Wird angelegt…", damit ein Doppelklick keine zweite Anfrage ausloest. Der Dialog bleibt offen,
   * "Abbrechen" bleibt bedienbar - ein haengender Aufruf soll ihn nicht festhalten. Der eigentliche
   * Riegel gegen zwei Klicks im selben Takt sitzt synchron im Handler (`App.tsx#teillieferungAnlegen`).
   */
  laeuft?: boolean;
  /**
   * Zaehler, den `App` nach einer 409 (veralteter Stand) und dem Nachladen erhoeht: aendert er sich,
   * werden NUR die Mengeneingaben (und eine stehende Fehlermeldung) zurueckgesetzt. Sie beziehen sich auf
   * den alten Stand und haengen an der Zeilenposition, die sich nach einer Teilung verschiebt. Die
   * Kopieroptionen ("Dokumente/Tasks uebernehmen") bleiben, wie der Nutzer sie gewaehlt hat - ein
   * Neuaufbau des ganzen Dialogs (`key`) setzte sie auf "angehakt" zurueck.
   */
  eingabenStand?: number;
}

interface MengenZeile {
  index: number;
  item: OrderItem;
  verfuegbar: number;
  eingabe: string;
}

export function TeillieferungDialog({
  bestellung,
  bestellungen,
  onTeilen,
  onSchliessen,
  laeuft = false,
  eingabenStand = 0,
}: TeillieferungDialogProps): JSX.Element {
  const positionen = (bestellung.items || []) as OrderItem[];
  const [eingaben, setEingaben] = useState<string[]>(() =>
    positionen.map(() => "0"),
  );
  const [fehler, setFehler] = useState("");
  const [dokumenteUebernehmen, setDokumenteUebernehmen] = useState(true);
  const [tasksUebernehmen, setTasksUebernehmen] = useState(true);
  // Zustand aus einer Prop ableiten (React-Muster "Zustand beim Rendern anpassen"): Mengeneingaben
  // zuruecksetzen, wenn `App` einen neuen Stand meldet - die Kopieroptionen oben bleiben unberuehrt.
  const [gesehenerStand, setGesehenerStand] = useState(eingabenStand);
  if (gesehenerStand !== eingabenStand) {
    setGesehenerStand(eingabenStand);
    setEingaben(positionen.map(() => "0"));
    setFehler("");
  }

  const zeilen: MengenZeile[] = positionen.map((item, index) => ({
    index,
    item,
    // Fachliche Vorsicht: `qty` kann fachlich 0 sein (Position bereits vollstaendig abgezogen) -
    // `Number(item.qty) || 0` faengt NaN/leer ab, 0 bleibt 0 (kein Wahrheitswert-Unterschied noetig,
    // da 0 ohnehin das gewuenschte Ergebnis waere).
    verfuegbar: Math.max(0, Number(item.qty) || 0),
    eingabe: eingaben[index] ?? "0",
  }));

  function eingabeAendern(index: number, wert: string): void {
    setEingaben((vorherige) => vorherige.map((v, i) => (i === index ? wert : v)));
  }

  // Rein informativ, siehe Kopfkommentar: bereits vorhandene Geschwister derselben Sendung.
  const sendungsGruppe = shipmentOrderGroup(bestellung, bestellungen).filter(
    (o) => o.id !== bestellung.id,
  );
  const istWurzel = splitRootOrderId(bestellung, bestellungen) === bestellung.id;

  function absenden(): void {
    if (laeuft) return;
    const uebertragen = zeilen
      .map((zeile) => ({ zeile, menge: Number(zeile.eingabe) || 0 }))
      // Woertlich wie das Original (`row.transfer > 0`, index.html:8207): eine Menge von genau 0
      // bedeutet "diese Position wird nicht mitgeteilt", kein Fehler.
      .filter((eintrag) => eintrag.menge > 0);

    if (!uebertragen.length) {
      setFehler("Bitte bei mindestens einem Artikel eine Teilmenge eintragen.");
      return;
    }
    const zuViel = uebertragen.find((eintrag) => eintrag.menge > eintrag.zeile.verfuegbar);
    if (zuViel) {
      setFehler(
        `Die Menge fuer "${String(zuViel.zeile.item.name || zuViel.zeile.item.sku || "diese Position")}" darf die Restmenge (${zuViel.zeile.verfuegbar}) nicht ueberschreiten.`,
      );
      return;
    }

    // Pruefbefund (Task 9): `schreibwegVon` liefert `null`, wenn eine Position WEDER `jtlPosId` NOCH
    // `kLieferantenBestellungPos` traegt - dann NICHT stillschweigend `NaN`/`0` an `onTeilen`
    // weiterreichen (der Backend-Schreibriegel bekaeme eine kaputte Positions-ID), sondern wie die
    // "Restmenge ueberschritten"-Pruefung oben: sichtbare Fehlermeldung, kein Absenden.
    const mitSchreibweg = uebertragen.map((eintrag) => ({
      ...eintrag,
      schreibweg: schreibwegVon(positionen, eintrag.zeile.index),
    }));
    const ohneSchreibweg = mitSchreibweg.find((eintrag) => eintrag.schreibweg === null);
    if (ohneSchreibweg) {
      setFehler(
        `Fuer "${String(ohneSchreibweg.zeile.item.name || ohneSchreibweg.zeile.item.sku || "diese Position")}" konnte keine Positions-ID ermittelt werden - bitte die Seite neu laden.`,
      );
      return;
    }

    setFehler("");
    onTeilen(
      String(bestellung.id),
      mitSchreibweg.map((eintrag) => ({
        sku: eintrag.zeile.item.sku,
        qty: eintrag.menge,
        posId: eintrag.schreibweg!.jtlPosId,
        // Roh, nicht `verfuegbar`: `Math.max(0, ...)` darf den Vergleichswert des Hubs nicht verfaelschen.
        angezeigteMenge: Number(eintrag.zeile.item.qty),
      })),
      { dokumente: dokumenteUebernehmen, tasks: tasksUebernehmen },
    );
  }

  return (
    <section
      className="board-editor-dialog partial-delivery-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="partialDeliveryTitle"
    >
      <div className="board-editor-head">
        <div className="board-editor-head-copy">
          <h2 id="partialDeliveryTitle">Teillieferung anlegen</h2>
          <p>
            Alle Bestelldaten werden kopiert. Die unten eingetragenen Mengen werden von der
            Ursprungsbestellung abgezogen.
          </p>
        </div>
        <button className="icon-btn" type="button" onClick={onSchliessen} title="Abbrechen">
          <Ikone name="x" />
        </button>
      </div>
      <div className="board-editor-body partial-delivery-body">
        <span className="new-order-ordernumber-hint">Nummer wird vergeben</span>
        {sendungsGruppe.length > 0 && (
          <div className="board-editor-note">
            {istWurzel ? "Bereits vorhandene Teillieferungen dieser Sendung" : "Teil der Sendung"}:{" "}
            {sendungsGruppe.map((o) => String(o.orderNo || o.id)).join(", ")}
          </div>
        )}
        <div className="partial-delivery-copy-options">
          <label>
            <input
              type="checkbox"
              checked={dokumenteUebernehmen}
              onChange={(event) => setDokumenteUebernehmen(event.target.checked)}
            />
            Dokumente übernehmen
          </label>
          <label>
            <input
              type="checkbox"
              checked={tasksUebernehmen}
              onChange={(event) => setTasksUebernehmen(event.target.checked)}
            />
            Tasks übernehmen
          </label>
        </div>
        <div className="partial-table-wrap">
          <table className="partial-table">
            <thead>
              <tr>
                <th>Artikel</th>
                <th>SKU</th>
                <th className="num">Bisher</th>
                <th className="num">In Teillieferung</th>
                <th className="num">Verbleibt</th>
              </tr>
            </thead>
            <tbody>
              {zeilen.length === 0 ? (
                <tr>
                  <td colSpan={5}>Diese Bestellung enthält keine Artikel.</td>
                </tr>
              ) : (
                zeilen.map((zeile) => {
                  const eingabeZahl = Number(zeile.eingabe) || 0;
                  const verbleibt = Math.max(0, zeile.verfuegbar - eingabeZahl);
                  const bezeichnung = String(
                    zeile.item.name || zeile.item.sku || `Position ${zeile.index + 1}`,
                  );
                  return (
                    <tr key={zeile.index}>
                      {/*
                       * Auftrag 4 - `partial-table` traegt weder `<colgroup>` noch positionsabhaengiges
                       * CSS (kein `nth-child`, `bestand.css:3553-3560`) - das Thumbnail sitzt deshalb
                       * IN dieser vorhandenen Zelle, genau wie in der Kalkulationstabelle
                       * (`Drawer/CalcTab/PositionsTabelle.tsx`, `td.ek-col-name`), statt eine eigene
                       * kopflose Spalte samt `<col>`/`<th>` einzufuehren. Klickbar (Vorgabe): dieser
                       * Dialog rendert selbst schon in einem geoeffneten `board-editor-overlay`
                       * (`App.tsx`) - genau wie die Anlagemaske (`PositionsEditor.tsx:419`), deren
                       * Thumbnail ebenfalls klickbar bleibt. Ein weiteres Grossbild-Overlay darueber
                       * ist kein Gestapel, sondern traegt zuverlaessig: das Portal haengt SPAETER an
                       * `document.body` an und gewinnt bei gleichem z-index ueber die
                       * Dokumentreihenfolge (siehe Kopfkommentar `Artikelbild.tsx:196-202`).
                       */}
                      <td>
                        <span className="partial-table-name-inner">
                          <Artikelbild kArtikel={zeile.item.kArtikel} name={bezeichnung} hatBild={zeile.item.hatBild} />
                          {String(zeile.item.name ?? "Artikel")}
                        </span>
                      </td>
                      <td className="mono">{String(zeile.item.sku ?? "—")}</td>
                      <td className="num mono">{NUM.format(zeile.verfuegbar)}</td>
                      <td className="num">
                        <input
                          type="number"
                          min={0}
                          max={zeile.verfuegbar}
                          step="any"
                          aria-label={`Menge für die Teillieferung ${bezeichnung}`}
                          value={zeile.eingabe}
                          onChange={(event) => eingabeAendern(zeile.index, event.target.value)}
                        />
                      </td>
                      <td className="num partial-remain">{NUM.format(verbleibt)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="new-order-error" role="alert">
          {fehler}
        </div>
      </div>
      <div className="board-editor-foot">
        <button className="btn-sec" type="button" onClick={onSchliessen}>
          <Ikone name="x" />
          Abbrechen
        </button>
        <button className="btn-primary" type="button" onClick={absenden} disabled={laeuft}>
          <Ikone name="split" />
          {laeuft ? "Wird angelegt…" : "Teillieferung anlegen"}
        </button>
      </div>
    </section>
  );
}
